import { h } from 'vue';
import type { Slots, VNodeChild } from 'vue';

import {
  CUSTOM_HEADER_SLOT_ID,
  HEADER_METADATA_SLOT_ID,
  HEADER_PREFIX_SLOT_ID,
} from '../../constants';
import type { GetHoveredLineResult } from '../../managers/InteractionManager';
import type {
  DiffLineAnnotation,
  FileDiffMetadata,
  MergeConflictResolution,
} from '../../types';
import { getLineAnnotationName } from '../../utils/getLineAnnotationName';
import { getMergeConflictActionSlotName } from '../../utils/getMergeConflictActionSlotName';
import {
  getMergeConflictActionAnchor,
  type MergeConflictDiffAction,
} from '../../utils/parseMergeConflictDiffFromFile';
import { GutterUtilitySlotStyles, MergeConflictSlotStyles } from '../constants';

// The component passes its raw `ctx.slots`; the public scoped-slot contract is
// documented by `DiffSlotsVue` / `UnresolvedFileSlotsVue` in ../types. The
// `conflict` slot is only ever populated by `UnresolvedFile`.
interface RenderDiffChildrenProps {
  fileDiff: FileDiffMetadata;
  slots: Slots;
  lineAnnotations: DiffLineAnnotation[] | undefined;
  getHoveredLine(): GetHoveredLineResult<'diff'> | undefined;
  actions?: (MergeConflictDiffAction | undefined)[];
  resolveConflict?(
    conflictIndex: number,
    resolution: MergeConflictResolution
  ): void;
}

// Build the light-DOM children for a `<diffs-container>` rendering a diff. This
// mirrors `renderFileChildren` but uses diff-flavored annotations and adds the
// merge-conflict action slots used by `UnresolvedFile`.
export function renderDiffChildren({
  fileDiff,
  slots,
  lineAnnotations,
  getHoveredLine,
  actions,
  resolveConflict,
}: RenderDiffChildrenProps): VNodeChild[] {
  const children: VNodeChild[] = [];

  if (slots.header != null) {
    children.push(
      h('div', { slot: CUSTOM_HEADER_SLOT_ID }, slots.header({ fileDiff }))
    );
  } else {
    if (slots.headerPrefix != null) {
      children.push(
        h(
          'div',
          { slot: HEADER_PREFIX_SLOT_ID },
          slots.headerPrefix({ fileDiff })
        )
      );
    }
    if (slots.headerMetadata != null) {
      children.push(
        h(
          'div',
          { slot: HEADER_METADATA_SLOT_ID },
          slots.headerMetadata({ fileDiff })
        )
      );
    }
  }

  if (slots.annotation != null && lineAnnotations != null) {
    const renderAnnotation = slots.annotation;
    lineAnnotations.forEach((annotation, index) => {
      children.push(
        h(
          'div',
          { key: index, slot: getLineAnnotationName(annotation) },
          renderAnnotation({ annotation })
        )
      );
    });
  }

  if (slots.conflict != null && actions != null && resolveConflict != null) {
    const renderConflict = slots.conflict;
    for (const action of actions) {
      if (action == null) {
        continue;
      }
      const slot = getConflictSlotName(action, fileDiff);
      if (slot == null) {
        continue;
      }
      children.push(
        h(
          'div',
          { key: slot, slot, style: MergeConflictSlotStyles },
          renderConflict({
            action,
            resolveConflict: (resolution: MergeConflictResolution) =>
              resolveConflict(action.conflictIndex, resolution),
          })
        )
      );
    }
  }

  if (slots.gutter != null) {
    children.push(
      h(
        'div',
        { slot: 'gutter-utility-slot', style: GutterUtilitySlotStyles },
        slots.gutter({ getHoveredLine })
      )
    );
  }

  return children;
}

// Resolve the named shadow-DOM slot for a merge-conflict action by anchoring it
// to the hunk/line it lives on. Returns undefined when the action has no anchor
// (e.g. the conflict was already resolved away).
function getConflictSlotName(
  action: MergeConflictDiffAction,
  fileDiff: FileDiffMetadata
): string | undefined {
  const anchor = getMergeConflictActionAnchor(action, fileDiff);
  return anchor != null
    ? getMergeConflictActionSlotName({
        hunkIndex: anchor.hunkIndex,
        lineIndex: anchor.lineIndex,
        conflictIndex: action.conflictIndex,
      })
    : undefined;
}
